/*
 * Client-side mirror of the legacy BFF `ApiErrorResponse` envelope.
 * After Phase 0 (BFF throws real tRPC errors), this is kept as a backstop
 * for any procedure still using z.any() output schema (none should remain).
 *
 * Defined locally — never import from server packages — so client code stays
 * free of server dependencies. The shape mirrors the old envelope that
 * `globalErrorMiddleware` used to return: `httpStatus` is
 * `{ code: number; message: string }` and `errors` is a non-empty array of
 * `{ code: string; message: string; stack?: string }`.
 */
export const AUTH_INFRASTRUCTURE_UNAVAILABLE =
  'AUTH_INFRASTRUCTURE_UNAVAILABLE';

export type ApiErrorEnvelope = {
  httpStatus: { code: number; message: string };
  errors: Array<{ code: string; message: string }>;
};

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/*
 * Full-shape guard for the legacy BFF error envelope. Validates `httpStatus.code` +
 * `httpStatus.message`, and that every entry in a non-empty `errors` array has
 * `code` + `message`, to minimize false positives.
 */
export function isApiErrorEnvelope(value: unknown): value is ApiErrorEnvelope {
  if (!isRecord(value)) return false;
  const { httpStatus, errors } = value;
  if (
    !isRecord(httpStatus) ||
    typeof httpStatus['code'] !== 'number' ||
    typeof httpStatus['message'] !== 'string'
  ) {
    return false;
  }
  if (!Array.isArray(errors) || errors.length === 0) return false;
  return errors.every(
    (e) =>
      isRecord(e) &&
      typeof e['code'] === 'string' &&
      typeof e['message'] === 'string',
  );
}

/**
 * Extract the HTTP status code from any error shape the client may receive.
 * Supports both legacy recovery-link errors (meta.apiError.httpStatus.code)
 * and standard tRPC errors (data.httpStatus, shape.data.httpStatus).
 *
 * Returns undefined if the error is not recognized or has no status code.
 */
export function apiErrorStatus(error: unknown): number | undefined {
  const e = error as {
    meta?: { apiError?: { httpStatus?: { code?: number } } };
    data?: { httpStatus?: number };
    shape?: { data?: { httpStatus?: number } };
  } | null;
  return (
    e?.meta?.apiError?.httpStatus?.code ??
    e?.data?.httpStatus ??
    e?.shape?.data?.httpStatus
  );
}

/**
 * Extract the tRPC error code from standard tRPC errors.
 * Returns undefined if the error is not a recognized tRPC error.
 */
export function apiErrorCode(error: unknown): string | undefined {
  const e = error as {
    data?: { code?: string };
  } | null;
  return e?.data?.code;
}

/**
 * Extract a stable domain error code from known client error shapes.
 * Prefer explicit fields, then legacy envelope codes, then exact enum-like
 * tRPC messages used when the current BFF transport cannot expose custom data.
 */
export function apiErrorDomainCode(error: unknown): string | undefined {
  const e = error as {
    message?: string;
    domainCode?: string;
    data?: { domainCode?: string };
    shape?: { data?: { domainCode?: string } };
    meta?: { apiError?: { errors?: Array<{ code?: string }> } };
  } | null;
  const explicit =
    e?.domainCode ??
    e?.data?.domainCode ??
    e?.shape?.data?.domainCode ??
    e?.meta?.apiError?.errors?.[0]?.code;
  if (explicit) {
    return explicit;
  }
  return typeof e?.message === 'string' && /^[A-Z0-9_]+$/.test(e.message)
    ? e.message
    : undefined;
}

/**
 * True when an error is a transient dynamic-import (route chunk) load failure
 * rather than a real application error. TanStack Router code-splits each route's
 * component into a lazily imported chunk; that import can fail transiently in
 * Vite dev (module reload / dep re-optimization) or in production right after a
 * deploy (the loaded index references a now-replaced hashed chunk). The wording
 * differs per browser, so match all known variants.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : ((error as { message?: unknown } | null)?.message ?? '');
  if (typeof message !== 'string') {
    return false;
  }
  return /dynamically imported module|Loading failed for the module|Importing a module script failed|Failed to fetch dynamically imported/i.test(
    message,
  );
}

/**
 * Extract a client-safe correlation id from an error, for the "Error reference"
 * bar on the 500 screen. Reads `data.requestId` (standard tRPC errors once the
 * BFF errorFormatter exposes `ctx.requestId`) and `meta.apiError.requestId`
 * (legacy recovery-link errors). Returns undefined when no id is available — in
 * which case the reference bar is not rendered. NEVER falls back to
 * stack/path/message.
 */
export function apiErrorReferenceId(error: unknown): string | undefined {
  const e = error as {
    data?: { requestId?: string };
    meta?: { apiError?: { requestId?: string } };
  } | null;
  return e?.data?.requestId ?? e?.meta?.apiError?.requestId;
}
